import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import { mergeUpdatedConversas } from '@modules/atendimento/utils/conversa-helpers'
import { msgKey } from '@modules/atendimento/store/mutations'
import { buildConversaPreviewPatch } from '@modules/atendimento/services/atendimento.service'
import { GlobalBus } from '@services/bus'
import { RELOAD } from '@constants/Text'

const buildOptimisticMessage = (id_conversa, params) => ({
  id: 'optimistic-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
  id_conversa,
  direcao: 'saida',
  tipo: params.tipo || 'texto',
  conteudo: params.conteudo || '',
  is_note: params.is_note || false,
  reply_to_message_id: params.reply_to_message_id || null,
  media_url: params.media_url || null,
  media_mime_type: params.media_mime_type || null,
  media_filename: params.media_filename || null,
  media_size_bytes: params.media_size_bytes || null,
  status: 'enviando',
  data_mensagem: new Date().toISOString(),
  _optimistic: true,
})

// Reconcilia a mensagem real (resposta do POST) com a optimistic, robusto
// às duas ordens de chegada (BUG 2 / ADR-3):
//  - se a real JÁ entrou no store via poll (mesma chave canônica, não
//    optimistic), removemos a optimistic duplicada;
//  - senão, substituímos a optimistic pela real.
const reconcileOptimisticComReal = ({ commit, state }, { tempId, real }) => {
  const realKey = msgKey(real)
  const jaExisteReal =
    realKey != null &&
    (state.mensagens || []).some((m) => !m._optimistic && msgKey(m) === realKey)
  if (jaExisteReal) {
    commit('REMOVE_MENSAGEM', tempId)
  } else {
    commit('REPLACE_OPTIMISTIC_MENSAGEM', { temp_id: tempId, real })
  }
}

/**
 * Aplica loading state antes da chamada API conforme flags _append / _silent.
 * Retorna os apiParams (sem flags internas) já com page incrementada se _append.
 */
function _applyLoadingState({ commit, state }, { _append, _silent }) {
  if (_append) {
    commit('SET_LOADING_MORE_CONVERSAS', true)
    return { page: (state.conversasPage || 1) + 1 }
  }
  if (!_silent) {
    commit('SET_LOADING_CONVERSAS', true)
  }
  return {}
}

/**
 * Limpa loading state no finally conforme a flag que ativou.
 */
function _clearLoadingState({ commit }, { _append, _silent }) {
  if (_append) {
    commit('SET_LOADING_MORE_CONVERSAS', false)
  } else if (!_silent) {
    commit('SET_LOADING_CONVERSAS', false)
  }
}

/**
 * Extrai valores seguros do response da API (list, page, has_more).
 * @param {object} r - Response da API
 * @param {number} [requestedPage] - Page que foi solicitada (frontend-authoritative).
 *   Quando fornecida, sobrepõe o echo do backend, prevenindo stall do scroll
 *   infinito se o backend omitir 'page'.
 */
function _parseConversasResponse(r, requestedPage) {
  if (!r || !r.value) {
    return { list: [], page: requestedPage || 1, hasMore: true }
  }
  const value = r.value
  // Suporta tanto response {conversas: [...]} quanto array bruto
  // (fallback compatível para callers que retornam value como array).
  const rawList = Array.isArray(value) ? value : value.conversas
  return {
    list: Array.isArray(rawList) ? rawList : [],
    page: requestedPage || value.page || 1,
    hasMore: value.has_more == null ? true : value.has_more,
  }
}

// `_silent: true` pula o commit de SET_LOADING_CONVERSAS — usado em
// refresh periódico em background (popup sync/polling) pra não piscar
// o skeleton quando o backend ainda está populando e retorna vazio.
// Primeira carga e ações de usuário (filtro, busca) chamam SEM _silent
// pra mostrar feedback visual normal.
export const listarConversas = async ({ commit, state }, params = {}) => {
  // apiParams repassa todo o payload (status/search/id_conexao e o escopo
  // da aba CRM: id_cliente_app/telefone) menos as flags internas.
  const { _silent, _append, ...apiParams } = params
  const flags = { _append, _silent }

  // Aplica loading e mescla page incrementada se for append
  Object.assign(apiParams, _applyLoadingState({ commit, state }, flags))
  const requestedPage = apiParams.page

  try {
    const r = await apiAtendimento.listarConversas(apiParams)
    if (!r || r.status !== 200) return r

    const { list, page, hasMore } = _parseConversasResponse(r, requestedPage)

    if (_append) {
      commit('APPEND_CONVERSAS', list)
    } else {
      commit('SET_CONVERSAS', list)
      // NÃO limpar a conversa selecionada só porque ela não está na página
      // atual retornada — é artefato de paginação, não deleção. Conversas
      // recém-criadas (sem mensagem) podem cair fora da página 1 pela
      // ordenação 'ultima_mensagem_em DESC NULLS LAST' e some errôneamente
      // após poucos segundos, fechando o chat que o usuário acabou de abrir.
      // A seleção só deve ser invalidada por um sinal explícito de deleção.
    }
    commit('SET_CONVERSAS_PAGE', page)
    commit('SET_CONVERSAS_HAS_MORE', hasMore)
    return r
  } finally {
    _clearLoadingState({ commit }, flags)
  }
}

// Limpa a lista global de conversas. Usado pela aba Omnichannel do CRM ao
// montar, pra não piscar as conversas de outro contexto (standalone/popup)
// enquanto o fetch escopado por cliente não retorna.
export const limparConversas = ({ commit }) => {
  commit('SET_CONVERSAS', [])
}

export const listarMensagens = async ({ commit }, params = {}) => {
  // When loading older messages (before_id), skip SET_MENSAGENS to avoid
  // replacing current messages. The caller (chat-window) will prepend.
  if (params.before_id) {
    const r = await apiAtendimento.listarMensagens(params)
    return r
  }
  commit('SET_LOADING_MENSAGENS', true)
  try {
    const r2 = await apiAtendimento.listarMensagens(params)
    if (r2 && r2.status === 200) {
      const mensagens = (r2.value && r2.value.mensagens) || r2.value || []
      commit('SET_MENSAGENS', Array.isArray(mensagens) ? mensagens : [])
      // Guarda o has_more da CARGA INICIAL para o chat-window saber, no 1º
      // scroll ao topo, se o banco já veio esgotado (has_more=false → cai
      // direto no import do WhatsApp) ou ainda tem páginas.
      const hasMore = r2.value && r2.value.has_more
      commit('SET_MENSAGENS_HAS_MORE', hasMore)
    }
    return r2
  } finally {
    commit('SET_LOADING_MENSAGENS', false)
  }
}

export const pollConversas = async ({ commit, state }, params = {}) => {
  const r = await apiAtendimento.pollConversas(params)
  if (r && r.status === 200 && r.value) {
    const updated = r.value.conversas_atualizadas || []
    if (updated.length > 0) {
      const current =
        (state.conversas && state.conversas.data) || state.conversas || []
      commit('SET_CONVERSAS', mergeUpdatedConversas(current, updated))
    }
  }
  return r
}

export const selectConversa = ({ commit }, conversa) => {
  commit('SET_MENSAGENS', [])
  // Reseta o has_more para o default (true) — a carga inicial da conversa nova
  // definirá o valor real. Sem isso, a conversa nova herdaria o has_more da
  // anterior até a 1ª carga retornar.
  commit('SET_MENSAGENS_HAS_MORE', true)
  commit('SET_SELECTED_CONVERSA', conversa)
  commit('SET_REPLY_TO', null)
  commit('SET_NOTE_MODE', false)
  commit('SET_EDITING_MESSAGE', null)
  commit('SET_UPLOAD_PROGRESS', null)
  // Reset dos dados por-conversa de M1/M2 ao trocar de conversa.
  commit('SET_LINKED_TASKS', {})
  commit('SET_LINKED_TASK_LIST', [])
  commit('SET_NOTAS_CONVERSA', [])
}

// M1 — commit-only: recebe a LISTA de vínculos (carregada no componente via API)
// e atualiza o mapa { [externalId]: taskId } (badge) + a lista (sidebar). A
// chamada de API vive no componente; a action apenas comita os dados.
export const setVinculosTarefa = ({ commit }, list) => {
  const safeList = Array.isArray(list) ? list : []
  const map = {}
  for (const item of safeList) {
    if (item && item.externalId) map[item.externalId] = item.taskId
  }
  commit('SET_LINKED_TASKS', map)
  commit('SET_LINKED_TASK_LIST', safeList)
}

// M2 — commit-only: recebe as notas (carregadas no componente via API).
export const setNotasConversa = ({ commit }, value) => {
  commit('SET_NOTAS_CONVERSA', Array.isArray(value) ? value : [])
}

// M2 — commit-only: estado de loading das notas (controlado pelo componente).
export const setLoadingNotas = ({ commit }, value) => {
  commit('SET_LOADING_NOTAS', !!value)
}

// Atualiza o preview/horário da conversa na lista lateral de forma
// MONOTÔNICA após o envio: se uma mensagem entrante mais nova já avançou
// `ultima_mensagem_em` (clock skew servidor), não regride para o timestamp
// da mensagem que acabamos de enviar. Quando a conversa não está na lista
// lateral (`{ id }` puro, sem timestamp), o patch sempre aplica — mesma
// semântica do comportamento anterior.
const updateSidebarPreview = ({ commit, state }, id_conversa, real) => {
  const conversas = state.conversas || []
  const conversaLateral = conversas.find(
    (c) => c && c.id === id_conversa
  ) || { id: id_conversa }
  const patch = buildConversaPreviewPatch(conversaLateral, [real])
  if (patch) commit('UPDATE_CONVERSA', patch)
}

// Efeitos de um envio bem-sucedido: reconcilia a otimista pela real (BUG 2,
// robusto às duas ordens de chegada), aplica o patch monotônico da lateral e —
// quando é NOTA interna — avisa a sidebar para recarregar a lista de notas (M2).
const applyEnvioSucesso = (
  { commit, state },
  { tempId, real, id_conversa, isNote }
) => {
  reconcileOptimisticComReal({ commit, state }, { tempId, real })
  updateSidebarPreview({ commit, state }, id_conversa, real)
  if (isNote && id_conversa) {
    GlobalBus.$emit(RELOAD.ATENDIMENTO_NOTAS, { id_conversa })
  }
}

export const enviarMensagem = async ({ commit, state }, params = {}) => {
  const id_conversa = state.selectedConversa
    ? state.selectedConversa.id
    : params.id_conversa

  const optimistic = buildOptimisticMessage(id_conversa, params)
  const tempId = optimistic.id
  commit('ADD_MENSAGEM', optimistic)

  try {
    const r = await apiAtendimento.enviarMensagem({
      id_conversa,
      tipo: params.tipo,
      conteudo: params.conteudo,
      is_note: params.is_note || false,
      reply_to_message_id: params.reply_to_message_id || null,
      media_url: params.media_url || null,
      media_mime_type: params.media_mime_type || null,
      media_filename: params.media_filename || null,
      media_size_bytes: params.media_size_bytes || null,
    })
    if (r && r.status === 200 && r.value) {
      applyEnvioSucesso(
        { commit, state },
        { tempId, real: r.value, id_conversa, isNote: params.is_note }
      )
    } else {
      // Keep the bubble but mark as error so the user can retry
      commit('UPDATE_MENSAGEM', { id: tempId, status: 'erro' })
    }
    return r
  } catch (e) {
    commit('UPDATE_MENSAGEM', { id: tempId, status: 'erro' })
    throw e
  }
}

export const checkDeliveryStatuses = async ({ commit, state }) => {
  const selected = state.selectedConversa
  if (!selected || !selected.id) return
  try {
    const r = await apiAtendimento.getDeliveryStatus({
      id_conversa: selected.id,
    })
    if (r && r.status === 200 && Array.isArray(r.value)) {
      commit('APPLY_DELIVERY_STATUSES', r.value)
    }
  } catch {
    // Polling de delivery status é best-effort; se falhar, os checks
    // continuam com o último valor conhecido até o próximo ciclo.
  }
}

export const marcarComoLida = async ({ commit }, params) => {
  if (!params) params = {}
  // Optimistic update: clear badge immediately (don't wait for API)
  commit('UPDATE_CONVERSA', {
    id: params.id_conversa,
    mensagens_nao_lidas: 0,
  })
  // Fire-and-forget API call
  try {
    await apiAtendimento.marcarComoLida(params)
  } catch {
    // Já aplicamos o update otimista no commit acima — uma falha aqui
    // será reconciliada no próximo poll (que reflete o estado real).
  }
}

export const uploadMedia = async (
  { commit, state },
  { id_conversa, tipo, arquivo } = {}
) => {
  commit('SET_UPLOAD_PROGRESS', 0)
  const r = await apiAtendimento.uploadMedia({
    id_conversa:
      id_conversa ||
      (state.selectedConversa ? state.selectedConversa.id : null),
    tipo,
    arquivo,
    onProgress: function (percent) {
      commit('SET_UPLOAD_PROGRESS', percent)
    },
  })
  commit('SET_UPLOAD_PROGRESS', null)
  return r
}

export const editarMensagem = async (
  { commit },
  { id_mensagem, conteudo } = {}
) => {
  const r = await apiAtendimento.editarMensagem({ id_mensagem, conteudo })
  if (r && r.status === 200 && r.value) {
    commit('UPDATE_MENSAGEM', r.value)
  }
  commit('SET_EDITING_MESSAGE', null)
  return r
}

export const removerMensagem = async ({ commit }, { id_mensagem } = {}) => {
  const r = await apiAtendimento.removerMensagem({ id_mensagem })
  if (r && r.status === 200) {
    commit('REMOVE_MENSAGEM', id_mensagem)
  }
  return r
}

export const importarHistorico = async (
  { commit, state },
  { id_conversa, quantidade } = {}
) => {
  const conversaId =
    id_conversa || (state.selectedConversa ? state.selectedConversa.id : null)
  const r = await apiAtendimento.importarHistorico({
    id_conversa: conversaId,
    quantidade,
  })
  return r
}

export const reagirMensagem = async (
  { commit, state },
  { id_mensagem, emoji } = {}
) => {
  // Save current reactions for rollback
  const msg = state.mensagens.find((m) => m.id === id_mensagem)
  const previousReacoes = msg ? (msg.reacoes || []).slice() : []

  // Optimistic: show reaction immediately
  const optimisticReacoes = previousReacoes.concat([{ emoji, by: 'Você' }])
  commit('UPDATE_MENSAGEM', { id: id_mensagem, reacoes: optimisticReacoes })

  const r = await apiAtendimento.reagirMensagem({ id_mensagem, emoji })
  if (r && r.status === 200 && r.value) {
    // Replace with server-confirmed reactions
    commit('UPDATE_MENSAGEM', {
      id: id_mensagem,
      reacoes: r.value.reacoes || optimisticReacoes,
    })
  } else {
    // Rollback on error
    commit('UPDATE_MENSAGEM', { id: id_mensagem, reacoes: previousReacoes })
  }
  return r
}

// Action PURA (sem chamada de API): mescla os dados retornados pelo backend
// (nome, foto_url, nome_contato, participantes, ...) no contato selecionado,
// preservando a identidade de `selectedConversa` (não dispara watchers de
// chat-window) e espelhando na lista lateral via UPDATE_CONVERSA. É dispatchada
// pela camada `apiAtendimento` após o request bem-sucedido (refresh de
// participantes/dados do contato, renomear) — a responsabilidade de salvar no
// Vuex fica na API; a action só aplica a mutação.
export const applyContatoUpdate = ({ commit, state }, { id_contato, value }) => {
  if (!value || typeof value !== 'object') return
  const selected = state.selectedConversa
  if (!selected || !selected.contato || selected.contato.id !== id_contato)
    return
  const updatedContato = { ...selected.contato, ...value }
  commit('SET_SELECTED_CONVERSA_CONTATO', updatedContato)
  commit('UPDATE_CONVERSA', { id: selected.id, contato: updatedContato })
}

export const setReplyTo = ({ commit }, message) => {
  commit('SET_EDITING_MESSAGE', null)
  commit('SET_REPLY_TO', message)
}

export const setNoteMode = ({ commit }, active) => {
  commit('SET_NOTE_MODE', active)
}

export const setEditingMessage = ({ commit }, message) => {
  commit('SET_REPLY_TO', null)
  commit('SET_EDITING_MESSAGE', message)
}

export const cancelReply = ({ commit }) => {
  commit('SET_REPLY_TO', null)
}

export const cancelEdit = ({ commit }) => {
  commit('SET_EDITING_MESSAGE', null)
}

export const prependMensagens = ({ commit }, mensagens) => {
  commit('PREPEND_MENSAGENS', mensagens)
}

// Shared helper for conversa status mutations (atribuir/fechar/escalar).
// Commits UPDATE_CONVERSA with the given patch and, when the affected
// conversation is the currently selected one, mirrors the patch into
// SET_SELECTED_CONVERSA so the open chat reflects the new status.
const updateConversaStatus = ({ commit, state }, { id_conversa, patch }) => {
  commit('UPDATE_CONVERSA', { id: id_conversa, ...patch })
  if (state.selectedConversa && state.selectedConversa.id === id_conversa) {
    commit('SET_SELECTED_CONVERSA', { ...state.selectedConversa, ...patch })
  }
}

export const atribuirConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const r = await apiAtendimento.atribuirConversa({ id_conversa: id_conversa })
  if (r && r.status === 200 && r.value) {
    updateConversaStatus({ commit, state }, { id_conversa, patch: r.value })
  }
  return r
}

export const fecharConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const r = await apiAtendimento.fecharConversa({ id_conversa: id_conversa })
  if (r && r.status === 200) {
    updateConversaStatus(
      { commit, state },
      { id_conversa, patch: { status: 'fechada' } }
    )
  }
  return r
}

export const escalarConversa = async ({ commit, state }, params) => {
  const id_conversa = params.id_conversa
  const nota = params.nota || ''
  const r = await apiAtendimento.escalarConversa({
    id_conversa: id_conversa,
    nota: nota,
  })
  if (r && r.status === 200) {
    updateConversaStatus(
      { commit, state },
      { id_conversa, patch: { status: 'escalada' } }
    )
  }
  return r
}

// Handler compartilhado pelos dois listeners de ACTION.ATENDIMENTO_CONVERSA_CRIADA
// (popup flutuante + view fullscreen). NÃO chamar listarConversas aqui: a nova
// conversa pode estar fora da página 1 (sem mensagem, ordenação por
// ultima_mensagem_em DESC com null no fim) e listarConversas chama
// clearSelectedConversa quando selected.id não é achado na página — fecharia
// o chat recém-aberto. pollConversas faz merge (preserva a seleção).
export const handleConversaCriada = async ({ dispatch }, data) => {
  if (!data || !data.id) return
  await dispatch('selectConversa', data)
  await dispatch('listarMensagens', { id_conversa: data.id })
  if (data.mensagens_nao_lidas && data.mensagens_nao_lidas > 0) {
    dispatch('marcarComoLida', { id_conversa: data.id })
  }
  dispatch('pollConversas')
}

export const transferirConversa = async ({ dispatch }, params) => {
  const id_conversa = params.id_conversa
  const para_profissional_id = params.para_profissional_id
  const nota = params.nota || ''
  const r = await apiAtendimento.transferirConversa({
    id_conversa: id_conversa,
    para_profissional_id: para_profissional_id,
    nota: nota,
  })
  if (r && r.status === 200) {
    dispatch('listarConversas')
  }
  return r
}

export const listarRespostasRapidas = async ({ commit }) => {
  const r = await apiAtendimento.listarRespostasRapidas()
  if (r && r.status === 200) {
    commit('SET_RESPOSTAS_RAPIDAS', r.value || [])
  }
  return r
}

export const setFiltroStatus = ({ commit }, status) => {
  commit('SET_FILTRO_STATUS', status)
}

export const setFiltroProfissional = ({ commit }, profissional) => {
  commit('SET_FILTRO_PROFISSIONAL', profissional)
}
